import { PostgrestError } from '@supabase/supabase-js';

/**
 * Type guard to check if a value is a PostgrestError
 */
export function isPostgrestError(value: any): value is PostgrestError {
  return value && typeof value === 'object' && 'code' in value && 'message' in value && 'details' in value;
}

/**
 * Type guard to check if an object is a SelectQueryError
 */
export function isSelectQueryError(obj: any): boolean {
  return obj && typeof obj === 'object' && obj.error === true && typeof obj.toString === 'function';
}

/**
 * Safely gets a property from a potentially error-containing Supabase response
 */
export function safelyGetProperty<T, K extends keyof T>(obj: T, key: K): T[K] | undefined {
  if (!obj || isSelectQueryError(obj)) {
    return undefined;
  }
  return obj[key];
}

/**
 * Safely access properties from Supabase response objects that might be error objects
 */
export function safeResponse<T extends Record<string, any>>(data: T | null | undefined): Partial<T> {
  if (!data || isSelectQueryError(data)) {
    return {};
  }
  return data;
}

/**
 * Helper function to filter out error responses from Supabase data arrays
 */
export function filterValidResponses<T>(data: (T | null | undefined)[]): T[] {
  return data.filter((item): item is T => 
    item !== null && item !== undefined && !isSelectQueryError(item)
  );
}

/**
 * Type-guard to ensure value is not a Supabase error response
 */
export function isNotError<T>(value: T | PostgrestError): value is T {
  return !isPostgrestError(value);
}

/**
 * Utility function to handle common Supabase error patterns
 */
export function handleSupabaseError(error: unknown): string {
  if (isPostgrestError(error)) {
    return `Database error: ${error.message}`;
  } else if (error instanceof Error) {
    return error.message;
  } else if (typeof error === 'string') {
    return error;
  }
  return 'An unknown error occurred';
}

/**
 * Validates if a string is a properly formatted UUID
 * @param uuid The string to validate as UUID
 * @returns boolean indicating if string is a valid UUID
 */
export const isValidUUID = (uuid: string): boolean => {
  if (!uuid) return false;
  // UUID format: 8-4-4-4-12 (hex digits)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

/**
 * Safely generates a UUID using the uuid library
 * @returns A valid UUID string
 */
export const generateSafeUUID = (): string => {
  const { v4: uuidv4 } = require('uuid');
  const uuid = uuidv4();
  
  if (!isValidUUID(uuid)) {
    console.error('Generated an invalid UUID:', uuid);
    throw new Error('Failed to generate valid UUID');
  }
  
  return uuid;
};
